/**
 * adminService.js — Admin API calls for user management, settings, quota, monitoring.
 *
 * All endpoints require a Bearer token (passed via getAuthHeaders()).
 */

const MANAGER_BASE = '/api/manager';

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse(response) {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${response.status})`);
  }
  return await response.json();
}

// ═══════════════════════════════════════════════════════════════════
// USER MANAGEMENT
// ═══════════════════════════════════════════════════════════════════

export async function listUsers() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/`, { headers: getAuthHeaders() }));
}

export async function getUser(userId) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}`, { headers: getAuthHeaders() }));
}

export async function createUser(userData) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/`, {
    method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(userData),
  }));
}

export async function updateUser(userId, userData) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(userData),
  }));
}

export async function deleteUser(userId) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}`, {
    method: 'DELETE', headers: getAuthHeaders(),
  }));
}

export async function toggleUserActive(userId) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}/toggle-active`, {
    method: 'POST', headers: getAuthHeaders(),
  }));
}

export async function approveUser(userId) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}/approve`, {
    method: 'POST', headers: getAuthHeaders(),
  }));
}

export async function rejectUser(userId) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/users/${userId}/reject`, {
    method: 'POST', headers: getAuthHeaders(),
  }));
}

// ═══════════════════════════════════════════════════════════════════
// AI TOKEN QUOTA
// ═══════════════════════════════════════════════════════════════════

export async function getMyQuota() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/ai-quota/me`, { headers: getAuthHeaders() }));
}

export async function getUserQuota(userId) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/ai-quota/user/${userId}`, { headers: getAuthHeaders() }));
}

export async function setUserQuota(userId, dailyQuota) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/ai-quota/user/${userId}`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ daily_token_quota: dailyQuota }),
  }));
}

export async function getQuotaDefaults() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/ai-quota/defaults`, { headers: getAuthHeaders() }));
}

export async function updateQuotaDefaults(defaults) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/ai-quota/defaults`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(defaults),
  }));
}

// ═══════════════════════════════════════════════════════════════════
// VALIDATION POLICY & ADMIN SETTINGS
// ═══════════════════════════════════════════════════════════════════

export async function getValidationPolicy() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/validation-policy/`, { headers: getAuthHeaders() }));
}

export async function updateValidationPolicy(data) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/validation-policy/`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
  }));
}

export async function getAllowedDomains() {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/admin/allowed-domains`, { headers: getAuthHeaders() }));
}

export async function updateAllowedDomains(domains) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/admin/allowed-domains`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ domains }),
  }));
}

// ═══════════════════════════════════════════════════════════════════
// MONITORING / AUDIT
// ═══════════════════════════════════════════════════════════════════

export async function getAIStats(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/ai-stats?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getAIToolStats(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/ai-tool-stats?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getUserActivity(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/user-activity?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getUsageTimeline(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/timeline?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getModelUsage(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/model-usage?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getAITotals(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/ai-totals?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getCostAnalysis(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/cost-analysis?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getAgentUsage(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/agent-usage?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getQueryStats(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/query-stats?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getDataSources(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/data-sources?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getTableUsage(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/table-usage?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getQueryTimeline(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/query-timeline?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getUserActivitySummary(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/user-activity-summary?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getUserActivityDetailed(days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/user-activity-detailed?days=${days}`, { headers: getAuthHeaders() }));
}

export async function getUserActivityDetail(userId, days = 30) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/user/${userId}/activity?days=${days}`, { headers: getAuthHeaders() }));
}

export async function logInteraction(data) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/log-interaction`, {
    method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
  }));
}

export async function logQuery(data) {
  return handleResponse(await fetch(`${MANAGER_BASE}/api/monitoring/log-query`, {
    method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
  }));
}

export default {
  listUsers, getUser, createUser, updateUser, deleteUser, toggleUserActive,
  approveUser, rejectUser,
  getMyQuota, getUserQuota, setUserQuota, getQuotaDefaults, updateQuotaDefaults,
  getValidationPolicy, updateValidationPolicy, getAllowedDomains, updateAllowedDomains,
  getAIStats, getAIToolStats, getUserActivity, getUsageTimeline, getModelUsage,
  getAITotals, getCostAnalysis, getAgentUsage,
  getQueryStats, getDataSources, getTableUsage, getQueryTimeline,
  getUserActivitySummary, getUserActivityDetailed, getUserActivityDetail,
  logInteraction, logQuery,
};
